/**
 * Local naming for saved printers.
 *
 * When a printer is connected and saved we give it a short, predictable local
 * name — `Printer #<N> - <TYPE>` (e.g. `Printer #1 - BLE`, `Printer #2 - NTWRK`)
 * — so a cashier can tell remembered printers apart without reading a model
 * string. The real model is kept on `SavedPrinter.model` and shown as secondary
 * text; this only sets the display `name`.
 */
import type { SavedPrinter, StarPrinterInterface } from 'src/services/printers';

/** Short, cashier-facing label for each connection interface. */
const INTERFACE_LABEL: Record<StarPrinterInterface, string> = {
  lan: 'NTWRK',
  bluetooth: 'BT',
  ble: 'BLE',
  usb: 'USB',
};

/** Uppercase connection-type label for a printer's interface (falls back to the raw type). */
export function interfaceLabel(interfaceType: StarPrinterInterface): string {
  return INTERFACE_LABEL[interfaceType] ?? interfaceType.toUpperCase();
}

const GENERATED_NAME_RE = /^Printer #(\d+)\b/;

/** Highest `#N` already used by a generated name, or 0 if none. */
function highestPrinterNumber(printers: SavedPrinter[]): number {
  return printers.reduce((max, printer) => {
    const match = printer.name?.match(GENERATED_NAME_RE);
    const n = match ? Number(match[1]) : 0;
    return n > max ? n : max;
  }, 0);
}

/**
 * Resolve the local name to persist for `printer` given the current remembered
 * list. Never overwrites the name of an existing record (the same printer being
 * re-selected keeps its name); only newly-saved printers get a generated name.
 */
export function resolvePrinterName(printer: SavedPrinter, remembered: SavedPrinter[]): string {
  const existing = remembered.find((p) => p.identifier === printer.identifier);
  if (existing?.name) return existing.name;

  const next = highestPrinterNumber(remembered) + 1 || remembered.length + 1;
  return `Printer #${next} - ${interfaceLabel(printer.interfaceType)}`;
}
